import { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { addSend } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels } from 'domains/campaigns/types';
import { CampaignStatus } from 'models/enums';
import useQueryParams from 'hooks/useQueryParams';
import { MAX_SENDS_AMOUNT, getSteps, renderChooseStepsForm } from '../utils';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignChooseNextStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState(null);
    const { editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    const maxSendsReached = campaignState.sends.length === MAX_SENDS_AMOUNT;

    const isEditingPausedCampaign = () => hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;

    const getIsStepDisabled = (step: CustomCampaignSteps) => {
        const activeSend = campaignState.sends[activeSendIndex] as any;
        const nextSend = campaignState.sends[activeSendIndex + 1] as any;
        const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;
        const isCompletedConditionalSplit = isActiveSendEmail && !!activeSend.splitType && !!activeSend.splitAction;

        // For paused curations: Time Delay must be selected first, then flow goes to content selection automatically
        // Email and Conditional Split are disabled here because user must go through Time Delay first
        if (isEditingPausedCampaign()) {
            const nextHasTimeDelay = !!nextSend?.parentAudienceDelayInterval;

            switch (step) {
                case CustomCampaignSteps.SELECT_CONTENT:
                    // Email is always disabled - user must go through Time Delay first
                    // Content selection happens automatically after time delay configuration
                    return true;

                case CustomCampaignSteps.SET_TIME_DELAY:
                    // Time Delay is enabled if next send has parentAudienceDelayInterval
                    return !nextHasTimeDelay;

                case CustomCampaignSteps.CONDITIONAL_SPLIT:
                    // Conditional Split is always disabled - user must go through Time Delay first
                    // Split configuration happens automatically after time delay configuration
                    return true;

                case CustomCampaignSteps.SCHEDULE_CURATION:
                    return false;
                default:
                    return false;
            }
        }

        // Default logic for non-paused curations
        switch (step) {
            case CustomCampaignSteps.SELECT_CONTENT:
                return true;
            case CustomCampaignSteps.SET_TIME_DELAY:
                return isCompletedConditionalSplit || maxSendsReached;
            case CustomCampaignSteps.CONDITIONAL_SPLIT:
                return true;
            case CustomCampaignSteps.SCHEDULE_CURATION:
                return false;
            default:
                return false;
        }
    };

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const step = (event.target as HTMLInputElement).value as CustomCampaignSteps;

        if (getIsStepDisabled(step)) return;

        setSelectedStep(step);

        if (step === CustomCampaignSteps.SET_TIME_DELAY) {
            const isLastSend = activeSendIndex === campaignState.sends.length - 1;
            if (isLastSend) {
                dispatchCampaignAction(addSend(activeSendIndex));
            }
            setActiveSendIndex(activeSendIndex + 1);
        }

        setActiveStep(step);
    };

    const steps = getSteps(getIsStepDisabled, maxSendsReached, isEditingPausedCampaign());

    return renderChooseStepsForm(handleStepClick, selectedStep, steps);
};

export default CustomCampaignChooseNextStep;
